import { useState, useEffect, useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { jobsAPI, candidatesAPI } from '../lib/api';
import { toast } from 'sonner';

// Data + polling + resume-upload state for the Job Detail page.
// Keeps all the candidate/upload synchronization (and the "no stale closure"
// functional-updater patterns) out of the page component.
export function useJobPolling(jobId) {
  const [job, setJob] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]); // per-file tracker
  const pollRef = useRef(null);
  const prevCompletedRef = useRef(new Set());

  const loadData = useCallback(async () => {
    try {
      const [jobRes, candRes] = await Promise.all([
        jobsAPI.get(jobId),
        candidatesAPI.list(jobId, 'overall_score', -1)
      ]);
      setJob(jobRes.data);
      setCandidates(candRes.data);
    } catch (err) {
      toast.error('Failed to load job details');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  const loadCandidates = useCallback(async (silent = false) => {
    try {
      const res = await candidatesAPI.list(jobId, 'overall_score', -1);
      setCandidates(res.data);
    } catch (err) {
      if (!silent && process.env.NODE_ENV === 'development') console.error(err);
    }
  }, [jobId]);

  useEffect(() => {
    loadData();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [loadData]);

  // Auto-poll while any candidate is processing
  useEffect(() => {
    const hasProcessing = candidates.some(c => ['uploaded', 'parsing', 'matching'].includes(c.status));
    const uploadsInProgress = uploadFiles.some(f => f.status !== 'completed' && f.status !== 'error');

    if ((hasProcessing || uploadsInProgress) && !pollRef.current) {
      pollRef.current = setInterval(() => {
        loadCandidates(true);
      }, 2000);
    } else if (!hasProcessing && !uploadsInProgress && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [candidates, uploadFiles, loadCandidates]);

  // Detect newly completed candidates and sync upload tracker.
  // Reads uploadFiles only inside the functional updater (no stale closure).
  useEffect(() => {
    const currentCompleted = new Set(candidates.filter(c => c.status === 'completed').map(c => c._id));
    const newlyDone = [...currentCompleted].filter(id => !prevCompletedRef.current.has(id));

    if (newlyDone.length > 0) {
      setUploadFiles(prev => {
        if (prev.length === 0) return prev;
        const updated = [...prev];
        for (const id of newlyDone) {
          const cand = candidates.find(c => c._id === id);
          if (cand) {
            const idx = updated.findIndex(
              f => f.candidateId === id || (f.status !== 'completed' && f.status !== 'error' && f.candidateName === cand.name)
            );
            if (idx !== -1) {
              updated[idx] = { ...updated[idx], status: 'completed', candidateId: id };
            }
          }
        }
        return updated;
      });
    }
    prevCompletedRef.current = currentCompleted;
  }, [candidates]);

  // Sync upload tracker with backend candidate statuses.
  // Reads uploadFiles only inside the functional updater (no stale closure).
  useEffect(() => {
    setUploadFiles(prev => {
      if (prev.length === 0) return prev;
      let changed = false;
      const updated = prev.map(f => {
        if (f.status === 'completed' || f.status === 'error' || f.status === 'uploading') return f;
        const cand = f.candidateId
          ? candidates.find(c => c._id === f.candidateId)
          : candidates.find(c => c.name === f.candidateName && c.status !== 'completed');
        if (cand && cand.status !== f.status) {
          changed = true;
          return {
            ...f,
            status: cand.status,
            candidateId: cand._id,
            errorMsg: cand.status === 'error' ? cand.error_message : undefined,
          };
        }
        return f;
      });
      return changed ? updated : prev;
    });
  }, [candidates]);

  const loadCandidatesRef = useRef(loadCandidates);
  loadCandidatesRef.current = loadCandidates;

  // Upload handler with per-file progress
  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    setUploading(true);

    const trackerEntries = acceptedFiles.map(f => ({
      name: f.name,
      status: 'uploading',
      uploadPct: 0,
      candidateId: null,
      candidateName: f.name.replace(/\.[^.]+$/, '').replace(/[_-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    }));
    setUploadFiles(prev => [...trackerEntries, ...prev]);

    for (let i = 0; i < acceptedFiles.length; i++) {
      const file = acceptedFiles[i];
      try {
        const res = await candidatesAPI.uploadResume(jobId, file, (progressEvent) => {
          const pct = progressEvent.total
            ? Math.round((progressEvent.loaded / progressEvent.total) * 100)
            : 0;
          setUploadFiles(prev => {
            const updated = [...prev];
            const idx = updated.findIndex(f => f.name === file.name && f.status === 'uploading');
            if (idx !== -1) updated[idx] = { ...updated[idx], uploadPct: pct };
            return updated;
          });
        });

        const candidateData = res.data;
        setUploadFiles(prev => {
          const updated = [...prev];
          const idx = updated.findIndex(f => f.name === file.name && (f.status === 'uploading'));
          if (idx !== -1) {
            updated[idx] = {
              ...updated[idx],
              status: 'uploaded',
              uploadPct: 100,
              candidateId: candidateData._id,
              candidateName: candidateData.name,
            };
          }
          return updated;
        });
      } catch (err) {
        setUploadFiles(prev => {
          const updated = [...prev];
          const idx = updated.findIndex(f => f.name === file.name && (f.status === 'uploading'));
          if (idx !== -1) {
            updated[idx] = {
              ...updated[idx],
              status: 'error',
              errorMsg: err.response?.data?.detail || 'Upload failed',
            };
          }
          return updated;
        });
      }
    }

    setUploading(false);
    toast.success(`${acceptedFiles.length} resume(s) uploaded. AI processing started.`);
    loadCandidatesRef.current();
  }, [jobId]);

  const dismissTracker = useCallback(() => {
    setUploadFiles(prev => prev.filter(f => f.status !== 'completed' && f.status !== 'error'));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt']
    },
    disabled: uploading
  });

  return {
    job, candidates, loading, uploading, uploadFiles,
    loadData, loadCandidates, dismissTracker,
    getRootProps, getInputProps, isDragActive,
  };
}
